import Editor from '@monaco-editor/react';
import { Check, ChevronLeft, Maximize2, Pencil } from 'lucide-react';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../../lib/utils.js';
import { Skeleton } from '../../atoms/skeleton.js';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../alert-dialog.js';
import { Sheet, SheetContent, SheetTitle } from '../sheet.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../tabs.js';

export interface FileEditorProps {
  path: string;
  content: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  defaultTab?: 'edit' | 'preview';
  className?: string;
  /** 'tabs' (default) shows the Edit/Preview tab switcher for markdown. 'editor' forces Monaco only. 'preview' forces rendered preview only. */
  mode?: 'tabs' | 'editor' | 'preview';
}

type EditorLanguage =
  | 'markdown'
  | 'shell'
  | 'typescript'
  | 'javascript'
  | 'python'
  | 'json'
  | 'yaml'
  | 'plaintext';

function detectLanguage(path: string): { language: EditorLanguage; isMarkdown: boolean } {
  const match = /\.([^.]+)$/.exec(path.toLowerCase());
  const extension = match?.[1] ?? '';

  if (extension === 'md') {
    return { language: 'markdown', isMarkdown: true };
  }

  if (extension === 'sh' || extension === 'bash') {
    return { language: 'shell', isMarkdown: false };
  }

  if (extension === 'ts' || extension === 'tsx') {
    return { language: 'typescript', isMarkdown: false };
  }

  if (extension === 'js' || extension === 'jsx') {
    return { language: 'javascript', isMarkdown: false };
  }

  if (extension === 'py') {
    return { language: 'python', isMarkdown: false };
  }

  if (extension === 'json') {
    return { language: 'json', isMarkdown: false };
  }

  if (extension === 'yaml' || extension === 'yml') {
    return { language: 'yaml', isMarkdown: false };
  }

  return { language: 'plaintext', isMarkdown: false };
}

function extractFrontmatter(content: string): { frontmatter: string | null; body: string } {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(content);
  if (!match) return { frontmatter: null, body: content };
  return { frontmatter: match[1], body: content.slice(match[0].length) };
}

function stripHtmlComments(text: string): string {
  return text.replace(/<!--[\s\S]*?-->/g, '').trim();
}

function MarkdownPreviewArticle({ content }: { content: string }) {
  const { frontmatter, body } = React.useMemo(
    () => extractFrontmatter(stripHtmlComments(content)),
    [content],
  );
  return (
    <article
      className={cn(
        'max-w-none text-sm leading-6 text-foreground',
        '[&_h1]:mb-3 [&_h1]:text-2xl [&_h1]:font-semibold',
        '[&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:text-xl [&_h2]:font-semibold',
        '[&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:text-lg [&_h3]:font-semibold',
        '[&_p]:my-3 [&_li]:my-1',
        '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6',
        '[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6',
        '[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground',
        '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs',
        '[&_pre]:my-4 [&_pre]:overflow-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:border-border [&_pre]:bg-muted [&_pre]:p-3',
        '[&_table]:w-full [&_table]:border-collapse',
        '[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
        '[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1',
      )}
    >
      {frontmatter && (
        <pre className="mb-4 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/50 px-3 py-2 font-mono text-xs text-muted-foreground">
          {frontmatter}
        </pre>
      )}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          table: ({ children }) => (
            <div className="my-4 overflow-x-auto">
              <table>{children}</table>
            </div>
          ),
        }}
      >
        {body}
      </ReactMarkdown>
    </article>
  );
}

function EditorLoadingState() {
  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-3">
      <Skeleton className="h-4 w-36" />
      <Skeleton className="h-full w-full" />
    </div>
  );
}

function MonacoPane({
  content,
  language,
  onChange,
  readOnly,
}: {
  language: EditorLanguage;
  content: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="h-full min-h-0">
      <Editor
        language={language}
        value={content}
        onChange={(value) => onChange(value ?? '')}
        theme="light"
        height="100%"
        loading={<EditorLoadingState />}
        options={{
          readOnly,
          automaticLayout: true,
          minimap: { enabled: false },
          fontSize: 13,
          lineNumbersMinChars: 3,
          wordWrap: 'on',
          scrollBeyondLastLine: false,
          scrollbar: {
            verticalScrollbarSize: 10,
            horizontalScrollbarSize: 0,
          },
        }}
      />
    </div>
  );
}

export function FileEditor({
  content,
  onChange,
  path,
  readOnly = false,
  defaultTab = 'edit',
  className,
  mode = 'tabs',
}: FileEditorProps) {
  const { isMarkdown, language } = React.useMemo(() => detectLanguage(path), [path]);
  const [markdownTab, setMarkdownTab] = React.useState<'edit' | 'preview'>(defaultTab);
  const [expanded, setExpanded] = React.useState(false);
  const [drawerEditing, setDrawerEditing] = React.useState(false);
  const [drawerContent, setDrawerContent] = React.useState(content);
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);

  React.useEffect(() => {
    setMarkdownTab(defaultTab);
  }, [path, defaultTab]);

  function openDrawer() {
    setDrawerContent(content);
    setDrawerEditing(false);
    setExpanded(true);
  }

  function handleDrawerClose(open: boolean) {
    if (!open) {
      setDrawerEditing(false);
      setExpanded(false);
    }
  }

  function handleBack() {
    if (drawerEditing) {
      if (drawerContent !== content) {
        setConfirmDiscard(true);
      } else {
        setDrawerEditing(false);
      }
    } else {
      setExpanded(false);
    }
  }

  function handleDiscardConfirmed() {
    setConfirmDiscard(false);
    setDrawerEditing(false);
    setDrawerContent(content);
  }

  function handleSave() {
    onChange(drawerContent);
    setDrawerEditing(false);
  }

  // ── Drawer content ────────────────────────────────────────────────────────

  function renderPreviewBody() {
    if (!isMarkdown || mode === 'editor') {
      return (
        <div className="flex-1 min-h-0">
          <MonacoPane
            content={content}
            language={language}
            onChange={onChange}
            readOnly={mode !== 'editor' || readOnly}
          />
        </div>
      );
    }
    return (
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-5 sm:p-6">
        <MarkdownPreviewArticle content={content} />
      </div>
    );
  }

  // ── Inline editor helpers ─────────────────────────────────────────────────

  function renderMonaco(opts?: { readOnly?: boolean }) {
    return (
      <MonacoPane
        content={content}
        language={language}
        onChange={onChange}
        readOnly={opts?.readOnly ?? readOnly}
      />
    );
  }

  function renderMarkdownScroll(extraClass?: string) {
    return (
      <div className={cn('overflow-y-auto overflow-x-hidden p-4', extraClass)}>
        <MarkdownPreviewArticle content={content} />
      </div>
    );
  }

  // ── Expand button (inline) ────────────────────────────────────────────────

  const expandButton = (
    <button
      type="button"
      aria-label="Expand view"
      onClick={openDrawer}
      className="absolute right-2 top-2 z-10 flex size-6 items-center justify-center rounded-md bg-background/80 text-muted-foreground opacity-50 backdrop-blur-sm transition-opacity hover:opacity-100 hover:bg-muted hover:text-foreground"
    >
      <Maximize2 className="size-3.5" />
    </button>
  );

  // ── Bottom drawer ─────────────────────────────────────────────────────────

  const drawer = (
    <Sheet open={expanded} onOpenChange={handleDrawerClose}>
      <SheetContent
        side="bottom"
        className="flex h-[95svh] flex-col rounded-t-2xl border-0 p-0 [&>button]:hidden"
        aria-describedby={undefined}
      >
        <SheetTitle className="sr-only">{path}</SheetTitle>

        {/* Header — app shell style */}
        <div className="flex shrink-0 items-center gap-3 rounded-t-2xl bg-primary/50 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] backdrop-blur-md">
          <button
            type="button"
            aria-label={drawerEditing ? 'Cancel editing' : 'Close'}
            onClick={handleBack}
            className="flex size-10 shrink-0 items-center justify-center rounded-full bg-black/20 text-white hover:bg-black/30"
          >
            <ChevronLeft className="size-5" />
          </button>

          <div className="flex min-w-0 flex-1 flex-col items-center justify-center">
            <p className="truncate text-sm font-semibold leading-tight text-primary-foreground">
              {path}
            </p>
            {drawerEditing && (
              <p className="text-xs leading-tight text-primary-foreground/70">Editing</p>
            )}
          </div>

          <div className="flex size-10 shrink-0 items-center justify-center">
            {drawerEditing ? (
              <button
                type="button"
                aria-label="Save"
                onClick={handleSave}
                className="flex size-10 items-center justify-center rounded-full bg-success text-success-foreground hover:bg-success/90"
              >
                <Check className="size-5" />
              </button>
            ) : (
              <button
                type="button"
                aria-label="Edit"
                onClick={() => {
                  setDrawerContent(content);
                  setDrawerEditing(true);
                }}
                className="flex size-10 items-center justify-center rounded-full bg-black/20 text-white hover:bg-black/30"
              >
                <Pencil className="size-5" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {drawerEditing ? (
            <MonacoPane
              content={drawerContent}
              language={language}
              onChange={setDrawerContent}
              readOnly={false}
            />
          ) : (
            renderPreviewBody()
          )}
        </div>
      </SheetContent>
    </Sheet>
  );

  const discardDialog = (
    <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard changes?</AlertDialogTitle>
          <AlertDialogDescription>
            Your edits to <span className="font-medium text-foreground">{path}</span> will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep editing</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDiscardConfirmed}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Discard
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // ── Inline render paths ───────────────────────────────────────────────────

  if (mode === 'editor') {
    return (
      <>
        {drawer}
        {discardDialog}
        <div
          className={cn(
            'relative h-full min-h-0 overflow-hidden rounded-md border border-border bg-background',
            className,
          )}
        >
          {expandButton}
          {renderMonaco()}
        </div>
      </>
    );
  }

  if (mode === 'preview') {
    if (!isMarkdown) {
      return (
        <>
          {drawer}
          {discardDialog}
          <div
            className={cn(
              'relative h-full min-h-0 overflow-hidden rounded-md border border-border bg-background',
              className,
            )}
          >
            {expandButton}
            {renderMonaco({ readOnly: true })}
          </div>
        </>
      );
    }
    return (
      <>
        {drawer}
        {discardDialog}
        <div
          className={cn(
            'relative h-full min-h-0 overflow-y-auto overflow-x-hidden rounded-md border border-border bg-background p-4',
            className,
          )}
        >
          {expandButton}
          <MarkdownPreviewArticle content={content} />
        </div>
      </>
    );
  }

  if (!isMarkdown) {
    return (
      <>
        {drawer}
        {discardDialog}
        <div
          className={cn(
            'relative h-full min-h-0 overflow-hidden rounded-md border border-border bg-background',
            className,
          )}
        >
          {expandButton}
          {renderMonaco()}
        </div>
      </>
    );
  }

  return (
    <>
      {drawer}
      {discardDialog}
      <div
        className={cn(
          'relative flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-border bg-background',
          className,
        )}
      >
        {expandButton}
        <Tabs
          value={markdownTab}
          onValueChange={(value) => setMarkdownTab(value as 'edit' | 'preview')}
          className="h-full min-h-0"
        >
          <TabsList className="mx-2 mt-2 w-auto">
            <TabsTrigger value="edit">Edit</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
          <TabsContent value="edit" className="mt-0 h-full min-h-0 flex-1">
            {renderMonaco()}
          </TabsContent>
          <TabsContent value="preview" className="mt-0 h-full min-h-0 flex-1">
            {renderMarkdownScroll('h-full')}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

export { detectLanguage };
